import React, { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { 
  MessageSquare, 
  Send, 
  Bot, 
  User, 
  X, 
  Minimize2,
  Maximize2,
  Phone,
  Mail,
  Clock
} from "lucide-react";

const AIChat = ({ isOpen, onToggle, userType = "customer", resellerId = null }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "ai",
      message: "Hello! I'm your AI assistant. How can I help you with your business card needs today?",
      timestamp: new Date().toISOString(),
      confidence: 1.0
    }
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentAgent, setCurrentAgent] = useState("AI Assistant");
  const messagesEndRef = useRef(null);

  // Pre-defined AI responses based on common questions
  const aiResponses = {
    "business card": "I can help you create professional business cards! Our system supports USA standard sizing (3.625\" × 2.125\"), multiple templates, photo integration, and high-resolution PDF generation. Would you like to start creating your card?",
    "template": "We have over 25+ professional templates in categories like Professional, Creative, Elegant, Corporate, and Nature themes. You can customize colors, fonts, and layouts. Would you like to see our template gallery?",
    "pricing": "Our business card packages start at $52.97 including design, HD PDF generation, and processing. Resellers get wholesale pricing. Would you like to see detailed pricing?",
    "reseller": "Our reseller program offers wholesale pricing, multi-level commissions up to 5 levels deep, and a $250 annual upgrade fee. You can earn 25% on direct sales plus team commissions. Interested in learning more?",
    "payment": "We accept credit/debit cards, PayPal, Square, and Zelle. We also offer over-the-phone payment processing for added security. Which payment method would you prefer?",
    "mlm": "Our MLM system offers 5-level deep commissions: Level 1 (25%), Level 2 (10%), Level 3 (5%), Level 4 (2%), Level 5 (1%). Plus achievement levels from Bronze to Platinum with bonus percentages.",
    "help": "I can assist with business card creation, templates, pricing, reseller program, payments, and technical support. What specific area would you like help with?",
    "support": "For immediate assistance, I'm here 24/7. For complex issues, I can connect you with our human support team. What type of support do you need?",
    "photo": "Yes! You can upload photos or take them directly with your device camera. Our system includes professional cropping tools and supports high-resolution images for crisp printing.",
    "size": "We use USA standard business card dimensions: 3.625\" × 2.125\" horizontal or 2.125\" × 3.625\" vertical, at 300 DPI for professional printing quality.",
    "language": "Our platform supports English and Spanish languages. You can switch languages using the language selector in the top navigation.",
    "discount": "I can help you with current promotions. We have seasonal discounts and special offers for new customers and resellers. Let me check what's available for you!",
    "default": "I understand you're asking about our services. Let me connect you with the most relevant information or a human agent who can better assist you."
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const findBestResponse = (message) => {
    const lowercaseMessage = message.toLowerCase();
    
    for (const [keyword, response] of Object.entries(aiResponses)) {
      if (keyword !== "default" && lowercaseMessage.includes(keyword)) {
        return { response, confidence: 0.9 };
      }
    }
    
    return { response: aiResponses.default, confidence: 0.6 };
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: "user",
      message: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsTyping(true);

    // Simulate AI processing time
    setTimeout(async () => {
      const { response, confidence } = findBestResponse(userMessage.message);
      
      const aiMessage = {
        id: Date.now() + 1,
        type: "ai",
        message: response,
        timestamp: new Date().toISOString(),
        confidence: confidence
      };

      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);

      // If confidence is low, suggest human agent
      if (confidence < 0.7) {
        setTimeout(() => {
          const followupMessage = {
            id: Date.now() + 2,
            type: "ai",
            message: "Would you like me to connect you with a human support agent for more personalized assistance?",
            timestamp: new Date().toISOString(),
            confidence: 1.0,
            actions: ["Connect to Human Agent", "Continue with AI"]
          };
          setMessages(prev => [...prev, followupMessage]);
        }, 1000);
      }
    }, 1500 + Math.random() * 1500); // Random delay for natural feel
  };

  const handleQuickAction = (action) => {
    if (action === "Connect to Human Agent") {
      const systemMessage = {
        id: Date.now(),
        type: "system",
        message: "Connecting you to a human support agent. Please wait a moment...",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, systemMessage]);
      setCurrentAgent("Human Agent (Connecting...)");
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) {
    return (
      <Button
        onClick={onToggle}
        className="fixed bottom-6 right-6 rounded-full w-16 h-16 bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 shadow-lg z-50"
      >
        <MessageSquare className="h-6 w-6 text-white" />
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-6 right-6 w-96 h-[600px] shadow-2xl z-50 flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-blue-600 rounded-full flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div>
              <CardTitle className="text-sm">{currentAgent}</CardTitle>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-xs text-gray-500">Online</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={() => setIsMinimized(!isMinimized)}
            >
              {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={onToggle}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {!isMinimized && (
        <>
          <CardContent className="flex-1 overflow-hidden p-0">
            <div className="h-full overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.type === "user"
                        ? "bg-gradient-to-r from-emerald-500 to-blue-600 text-white"
                        : message.type === "system"
                        ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    <div className="flex items-start space-x-2">
                      {message.type !== "user" && (
                        <div className="flex-shrink-0 mt-0.5">
                          {message.type === "ai" ? (
                            <Bot className="h-4 w-4" />
                          ) : (
                            <User className="h-4 w-4" />
                          )}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm">{message.message}</p>
                        {message.confidence && message.confidence < 0.8 && message.type === "ai" && (
                          <Badge variant="outline" className="mt-2 text-xs">
                            Confidence: {Math.round(message.confidence * 100)}%
                          </Badge>
                        )}
                        {message.actions && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {message.actions.map((action, index) => (
                              <Button
                                key={index}
                                size="sm"
                                variant="outline"
                                className="text-xs h-6"
                                onClick={() => handleQuickAction(action)}
                              >
                                {action}
                              </Button>
                            ))}
                          </div>
                        )}
                        <p className="text-xs opacity-70 mt-1">
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg p-3 max-w-[80%]">
                    <div className="flex items-center space-x-2">
                      <Bot className="h-4 w-4 text-gray-600" />
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </CardContent>

          {/* Quick Actions */}
          <div className="p-3 border-t bg-gray-50">
            <div className="flex space-x-2 mb-3">
              <Button size="sm" variant="outline" className="text-xs" onClick={() => handleQuickAction("Pricing Info")}>
                Pricing
              </Button>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => handleQuickAction("Templates")}>
                Templates
              </Button>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => handleQuickAction("MLM Info")}>
                MLM
              </Button>
            </div>
          </div>

          {/* Message Input */}
          <div className="p-4 border-t">
            <div className="flex space-x-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                className="flex-1"
              />
              <Button onClick={handleSendMessage} disabled={!inputMessage.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
              <span>Powered by AI • Response time ~2s</span>
              <div className="flex items-center space-x-3">
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                  <Phone className="h-3 w-3 mr-1" />
                  Call
                </Button>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                  <Mail className="h-3 w-3 mr-1" />
                  Email
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default AIChat;